import { nanoid } from 'nanoid';

import type { IconProps } from '@/ui/Icons/IconProps';

type HexColor = `#${string}`;

/**
 * Liquidity score icons keyed by their respective scores
 */
const BaseDroplet = (props: IconProps & { gradientColors: [HexColor, HexColor] }) => {
  const { gradientColors, ...restProps } = props;
  const [color1, color2] = gradientColors;

  // each instance needs a unique ID
  const uniqueId = nanoid();
  const filterId = `filter_${uniqueId}`;
  const gradientId1 = `paint0_${uniqueId}`;
  const gradientId2 = `paint1_${uniqueId}`;

  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={restProps.className}
      height={restProps.height ?? 16}
      width={restProps.width ?? 16}
      style={{
        ...restProps.style,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <g filter={`url(#${filterId})`}>
        <path
          d="M10.6421 6.11156C10.5174 5.48833 9.97021 5.03971 9.33464 5.03971C8.69906 5.03971 8.15184 5.48833 8.02719 6.11156C7.70235 7.73579 6.73793 9.31358 5.44838 10.3452C3.65749 11.7779 2.66797 13.6317 2.66797 15.6397C2.66797 19.3188 5.64776 22.373 9.33464 22.373C13.0215 22.373 16.0013 19.3188 16.0013 15.6397C16.0013 13.6077 14.9889 11.7875 13.2308 10.3532C11.9389 9.29928 10.9652 7.72717 10.6421 6.11156Z"
          fill={`url(#${gradientId1})`}
        />
        <path
          d="M18.668 2C19.3036 2 19.8508 2.44861 19.9754 3.07185C20.5781 6.08505 22.4024 9.01336 24.8343 10.9588C27.7396 13.2831 29.3337 16.1709 29.3347 19.3306C29.3428 21.4367 28.7257 23.4979 27.5615 25.2531C26.3968 27.0091 24.7369 28.3797 22.7923 29.1912C21.4796 29.739 20.077 30.0153 18.6681 30.0118C17.2592 30.0153 15.8565 29.739 14.5437 29.1912C13.0948 28.5865 11.8018 27.6681 10.7565 26.5151C10.3927 26.1138 10.2108 25.9132 10.1863 25.7002C10.1648 25.512 10.2212 25.3299 10.3455 25.1869C10.4861 25.0251 10.8157 24.9469 11.4747 24.7905C15.6207 23.8066 18.668 20.0303 18.668 15.6397C18.668 13.0205 17.5137 10.7631 15.7359 9.01938C15.4614 8.75015 15.3241 8.61553 15.2721 8.48817C15.2209 8.36292 15.2096 8.26688 15.2305 8.13319C15.2517 7.99724 15.3525 7.83704 15.5542 7.51666C16.4221 6.13792 17.0517 4.61603 17.3606 3.07185C17.4852 2.44861 18.0324 2 18.668 2Z"
          fill={`url(#${gradientId2})`}
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="2.66797"
          y="2"
          width="26.667"
          height="28.0117"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="-1" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
        </filter>
        <linearGradient
          id={gradientId1}
          x1="36.0014"
          y1="30.0118"
          x2="-2.54026"
          y2="0.847125"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={color1} />
          <stop offset="1" stopColor={color2} />
        </linearGradient>
        <linearGradient
          id={gradientId2}
          x1="36.0014"
          y1="30.0118"
          x2="-2.54026"
          y2="0.847125"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={color1} />
          <stop offset="1" stopColor={color2} />
        </linearGradient>
      </defs>
    </svg>
  );
};

export const DropletsIcon = {
  Unknown: (props: IconProps) => <BaseDroplet {...props} gradientColors={['#B7B7B7', '#C8C8C8']} />,
  High: (props: IconProps) => <BaseDroplet {...props} gradientColors={['#3EBE6A', '#1EDE60']} />,
  Medium: (props: IconProps) => <BaseDroplet {...props} gradientColors={['#F97F12', '#FFBC65']} />,
  Low: (props: IconProps) => <BaseDroplet {...props} gradientColors={['#F55E66', '#FF848A']} />,
};
